import type { ReactElement, RefObject } from 'react'
import React, { useRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import type { PressEvent } from '@react-types/shared'
import Spinner from '@ui-kit/components/Spinner/index.web'
import { buttonLabel } from '@ui-kit/core/typography'
import { borderRadius } from '@ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@ui-kit/styles/colors'
import type { Color } from '@ui-kit/types'

import * as styles from './styles'
import type { LabelProps, WebProps } from './types'

// The reason why there are two different Container depending on the tag,
// is because of this Styled Components issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/30117
// The as prop is not properly changing the element type

const Container = (props: WebProps & { children: React.ReactNode; className: string }) => {
  const { focusProps } = useFocusRing()
  const { onClick, onPress, ...restProps } = props

  const pressHandler = onClick ?? onPress

  const rel = `${props.openInNewTab ? 'noopener noreferrer' : ''} ${
    props.noFollow ? 'nofollow' : ''
  }`.trim()
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null)
  const handleOnClick = (event: PressEvent) => {
    if (props.isLoading || props.disabled) {
      return
    }

    if (pressHandler) {
      pressHandler(event)
    }
  }

  const { buttonProps } = useButton(
    {
      ...restProps,
      isDisabled: restProps.disabled,
      elementType: props.href === undefined ? 'button' : 'a',
      target: props.href && props.openInNewTab ? '_blank' : undefined,
      rel,
      onPress: handleOnClick,
      type: props.submit ? 'submit' : 'button',
    },
    ref as React.RefObject<Element>
  )

  const commonProps = {
    disabled: props.disabled,
    className: props.className,
    key: props.id,
    'data-testid': props.id,
    'data-loading': props.isLoading,
    children: props.children,
    ...focusProps,
  }

  if (props.href) {
    return <a {...commonProps} ref={ref as RefObject<HTMLAnchorElement>} />
  }

  return <button {...commonProps} {...buttonProps} ref={ref as RefObject<HTMLButtonElement>} />
}

const ButtonContent = (props: LabelProps & { labelColor: Color; iconContainerClass: string }) => (
  <>
    {props.iconLeft ? <div className={props.iconContainerClass}>{props.iconLeft}</div> : null}
    <span className={buttonLabel({ color: props.labelColor })}>{props.label}</span>
    {props.iconRight ? <div className={props.iconContainerClass}>{props.iconRight}</div> : null}
  </>
)

const Button = (props: WebProps): ReactElement => {
  const labelColor = styles.getLabelColor(props.type, props.variantColor)
  const { container, labelContainer, iconContainer } = styles.button({
    disabled: props.disabled,
    size: props.size,
    isLoading: props.isLoading,
  })
  const variantColor = props.variantColor ?? 'deepForest'
  const type = props.type ?? 'default'
  const radiusClass = props.radius && borderRadius({ radius: props.radius })
  return (
    <div
      className={container({
        className: twMerge(
          'p-0',
          type === 'default' && bgColor({ color: variantColor }),
          type === 'outlined' && ['border border-solid', borderColor({ color: variantColor })],
          radiusClass
        ),
      })}
    >
      <Container {...props} className={twMerge(container(), 'w-full', radiusClass)}>
        {props.isLoading && <Spinner fullScreen color={labelColor} size="small" />}
        <div className={labelContainer()}>
          {'icon' in props ? (
            <div className={iconContainer()}>{props.icon}</div>
          ) : (
            <ButtonContent
              iconContainerClass={iconContainer()}
              label={props.label ?? ''}
              labelColor={labelColor}
              iconLeft={props.iconLeft}
              iconRight={props.iconRight}
            />
          )}
        </div>
      </Container>
    </div>
  )
}

export default Button
